import React, { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { fetchProducts } from "../lib/api";
import { ProductCard } from "../components/ProductCard";
import axios from "axios";

const SKELETON_KEYS = ["a", "b", "c", "d", "e", "f", "g", "h"];
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const VALID_SECTIONS = ["women", "men", "fragrance"];

function ShopGridContent({ loading, products, t }) {
    if (loading) {
        return (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-12">
                {SKELETON_KEYS.map((id) => (
                    <div key={id} className="animate-pulse">
                        <div className="aspect-[3/4] bg-[#141414] border border-white/[0.04]" />
                        <div className="h-4 bg-[#141414] mt-4 w-1/2" />
                        <div className="h-3 bg-[#141414] mt-2 w-1/4" />
                    </div>
                ))}
            </div>
        );
    }
    if (products.length === 0) {
        return (
            <div className="text-center py-32">
                <p className="text-overline text-[#666]">{t("shop.empty_typology")}</p>
            </div>
        );
    }
    return (
        <>
            <div className="text-overline text-[#666] mb-8" data-testid="results-count">
                {t("shop.pieces", { count: products.length })}
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-12">
                {products.map((p, i) => (
                    <ProductCard key={p.id} product={p} index={i} />
                ))}
            </div>
        </>
    );
}

const SORT_OPTIONS = [
    { id: "featured", key: "sort_featured" },
    { id: "price-low", key: "sort_price_low" },
    { id: "price-high", key: "sort_price_high" },
    { id: "new", key: "sort_new" },
];

const SORT_FNS = {
    "price-low": (a, b) => a.price - b.price,
    "price-high": (a, b) => b.price - a.price,
    "new": (a, b) => Number(b.is_new) - Number(a.is_new),
    "featured": (a, b) => Number(b.is_featured) - Number(a.is_featured),
};

export default function Shop() {
    const { t } = useTranslation();
    const { section: sectionParam } = useParams();
    const [params, setParams] = useSearchParams();

    // Default landing on /shop → redirect to /shop/women
    if (!sectionParam) {
        return <Navigate to="/shop/women" replace />;
    }
    if (!VALID_SECTIONS.includes(sectionParam)) {
        return <Navigate to="/shop/women" replace />;
    }

    return <ShopSection section={sectionParam} params={params} setParams={setParams} t={t} />;
}

function ShopSection({ section, params, setParams, t }) {
    const category = params.get("c") || "all";
    const queryStr = (params.get("q") || "").trim().toLowerCase();
    const [categories, setCategories] = useState([]);
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [sortBy, setSortBy] = useState("featured");

    useEffect(() => {
        axios.get(`${BACKEND}/api/sections`).then((res) => {
            const sec = res.data?.sections?.find((s) => s.slug === section);
            setCategories(sec?.categories || []);
        }).catch(() => setCategories([]));
    }, [section]);

    useEffect(() => {
        setLoading(true);
        fetchProducts(category, { section })
            .then(setProducts)
            .finally(() => setLoading(false));
    }, [section, category]);

    const sorted = useMemo(() => {
        let arr = [...products];
        if (queryStr) {
            arr = arr.filter((p) =>
                [p.name, p.category, p.fabric, p.description].join(" ").toLowerCase().includes(queryStr)
            );
        }
        const fn = SORT_FNS[sortBy];
        if (fn) arr.sort(fn);
        return arr;
    }, [products, sortBy, queryStr]);

    const setCategory = (slug) => {
        const next = {};
        if (slug !== "all") next.c = slug;
        if (queryStr) next.q = queryStr;
        setParams(next);
    };

    const clearSearch = () => {
        const next = {};
        if (category !== "all") next.c = category;
        setParams(next);
    };

    const categoryLabel = (c) => {
        // For fragrance, subcategories are women/men/unisex — translate from shop.* if available
        const sectionKey = `nav.${section}`;
        const subKey = section === "fragrance" ? `shop.frag_${c.slug}` : `shop.filter_${c.slug}`;
        const fallback = c.label;
        // Use special "all" label
        if (c.slug === "all") return t("shop.filter_all", { defaultValue: "All" });
        return t(subKey, { defaultValue: fallback });
    };

    const heroTitle = t(`shop.section_title_${section}`, { defaultValue: t(`nav.${section}`) });
    const heroSubtitle = t(`shop.section_subtitle_${section}`, { defaultValue: t("shop.subtitle") });

    return (
        <div data-testid="shop-page" className="pt-28">
            <section className="container-rail pb-12 border-b border-white/5">
                <div className="grid grid-cols-12 gap-6 items-end">
                    <div className="col-span-12 md:col-span-8">
                        <div className="text-overline text-[#A3A3A3] mb-4">{t("shop.heading_label")}</div>
                        <h1 className="text-display text-5xl sm:text-6xl md:text-7xl leading-[0.92]" data-testid={`shop-title-${section}`}>
                            {heroTitle}.
                        </h1>
                    </div>
                    <div className="col-span-12 md:col-span-4 md:text-right">
                        <p className="text-[#A3A3A3] max-w-sm md:ml-auto">
                            {heroSubtitle}
                        </p>
                    </div>
                </div>
                {queryStr && (
                    <div className="mt-8 flex items-center gap-3 flex-wrap" data-testid="search-active">
                        <span className="text-overline text-[#A3A3A3]">{t("shop.searching")}</span>
                        <span className="px-3 py-1.5 border border-white/15 text-sm tracking-wide font-display">
                            "{queryStr}"
                        </span>
                        <button onClick={clearSearch} data-testid="search-clear" className="text-overline text-[#591C1C] hover:text-[#F4F4F0]">
                            {t("shop.clear")}
                        </button>
                    </div>
                )}
            </section>

            <section className="sticky top-16 z-20 bg-[#0C0C0C]/85 backdrop-blur-xl border-b border-white/5">
                <div className="container-rail py-4 flex flex-wrap items-center justify-between gap-4">
                    <div className="flex flex-wrap gap-1">
                        {categories.map((c) => (
                            <button
                                key={c.slug}
                                data-testid={`filter-${section}-${c.slug}`}
                                onClick={() => setCategory(c.slug)}
                                className={`px-4 py-2 text-[10px] tracking-[0.22em] uppercase font-display transition-all border ${
                                    category === c.slug
                                        ? "bg-[#EDEDE6] text-[#0C0C0C] border-[#EDEDE6]"
                                        : "border-white/10 text-[#A3A3A3] hover:text-[#F4F4F0] hover:border-white/30"
                                }`}
                            >
                                {categoryLabel(c)}
                            </button>
                        ))}
                    </div>
                    <div className="flex items-center gap-3">
                        <span className="text-overline text-[#666]">{t("shop.sort")}</span>
                        <select
                            data-testid="sort-select"
                            value={sortBy}
                            onChange={(e) => setSortBy(e.target.value)}
                            className="bg-transparent border border-white/10 px-3 py-2 text-xs text-[#F4F4F0] focus:outline-none focus:border-white/40 font-display tracking-wider"
                        >
                            {SORT_OPTIONS.map((s) => (
                                <option key={s.id} value={s.id} className="bg-[#141414]">
                                    {t(`shop.${s.key}`)}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
            </section>

            <section className="container-rail py-12 min-h-[60vh]">
                <ShopGridContent loading={loading} products={sorted} t={t} />
            </section>
        </div>
    );
}
